import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchAsset, fetchAssetRelationships, fetchAssetImpact } from '@/lib/api';
import { StatusDot } from '@/components/shared/StatusDot';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowLeft, Server, ArrowRight, AlertTriangle, Zap, Network } from 'lucide-react';

const REL_COLORS = {
  DEPENDS_ON: '#EF4444',
  CONNECTED_TO: '#F59E0B',
  HOSTED_ON: '#06B6D4',
  MONITORS: '#6366F1',
  ROUTES_TO: '#10B981',
};

export default function AssetDetailPage() {
  const { id } = useParams();
  const [showImpact, setShowImpact] = useState(false);

  const { data: asset, isLoading } = useQuery({ queryKey: ['asset', id], queryFn: () => fetchAsset(id) });
  const { data: relData } = useQuery({ queryKey: ['asset-rels', id], queryFn: () => fetchAssetRelationships(id), enabled: !!id });
  const { data: impactData } = useQuery({ queryKey: ['asset-impact', id], queryFn: () => fetchAssetImpact(id), enabled: showImpact });

  if (isLoading) return <div className="space-y-4"><div className="h-8 w-48 bg-[var(--nexus-bg-elevated)] rounded animate-pulse" /><div className="h-64 bg-[var(--nexus-bg-elevated)] rounded-xl animate-pulse" /></div>;
  if (!asset || asset.error) return <div className="flex flex-col items-center gap-4 h-full justify-center"><p className="text-[var(--nexus-text-muted)]">Asset not found</p><Link to="/assets" className="text-xs text-[var(--nexus-accent)] hover:underline">Back to Assets</Link></div>;

  return (
    <div data-testid="asset-detail-page" className="w-full max-w-[1600px] mx-auto">
      <Link to="/assets" className="flex items-center gap-1 text-xs text-[var(--nexus-text-muted)] hover:text-[var(--nexus-text-secondary)] transition-colors mb-4" data-testid="back-to-assets">
        <ArrowLeft size={14} /> Back to Assets
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Asset info */}
        <div className="nexus-card relative overflow-hidden p-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-[var(--nexus-accent)] bg-opacity-10 flex items-center justify-center">
              <Server size={22} className="text-[var(--nexus-accent)]" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-semibold text-[var(--nexus-text-primary)]">{asset.name}</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <StatusDot status={asset.status === 'ACTIVE' ? 'active' : 'warning'} pulse={asset.status === 'ACTIVE'} size="sm" />
                <span className="text-xs text-[var(--nexus-text-secondary)]">{asset.status}</span>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <InfoRow label="Type" value={asset.type} />
            <InfoRow label="Environment" value={asset.environment} />
            <InfoRow label="Owner" value={asset.owner || 'Unassigned'} />
            <InfoRow label="Region" value={asset.metadata?.region || '-'} />
            <InfoRow label="Cost Center" value={asset.metadata?.cost_center || '-'} />
          </div>
          <button onClick={() => setShowImpact(true)} className="mt-4 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[var(--nexus-danger)] bg-opacity-10 border border-red-500/20 text-red-300 text-xs font-medium hover:bg-opacity-20 transition-colors" data-testid="show-impact">
            <Zap size={12} /> Analyze Impact Radius
          </button>
        </div>

        {/* Relationships */}
        <div className="lg:col-span-2">
          <div className="nexus-card relative overflow-hidden">
            <div className="p-3 border-b border-[var(--nexus-border)]">
              <div className="flex items-center gap-2">
                <Network size={14} className="text-[var(--nexus-accent)]" />
                <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Dependency Topology</h2>
              </div>
            </div>
            <div className="p-4">
              {/* Outgoing */}
              {relData?.outgoing?.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium mb-2">Depends On / Connected To</h3>
                  <div className="space-y-1.5">
                    {relData.outgoing.map((rel) => (
                      <div key={rel.id} className="flex items-center gap-2 p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                        <span className="font-mono text-xs text-[var(--nexus-text-primary)]">{asset.name}</span>
                        <ArrowRight size={12} style={{ color: REL_COLORS[rel.type] || '#94A3B8' }} />
                        <span className="text-[10px] px-1.5 py-0.5 rounded border" style={{ borderColor: REL_COLORS[rel.type] || '#1E2A45', color: REL_COLORS[rel.type] || '#94A3B8', backgroundColor: `${REL_COLORS[rel.type] || '#94A3B8'}15` }}>{rel.type}</span>
                        <ArrowRight size={12} style={{ color: REL_COLORS[rel.type] || '#94A3B8' }} />
                        <span className="font-mono text-xs text-[var(--nexus-accent)]">{rel.target_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Incoming */}
              {relData?.incoming?.length > 0 && (
                <div>
                  <h3 className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium mb-2">Depended On By</h3>
                  <div className="space-y-1.5">
                    {relData.incoming.map((rel) => (
                      <div key={rel.id} className="flex items-center gap-2 p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                        <span className="font-mono text-xs text-[var(--nexus-accent)]">{rel.source_name}</span>
                        <ArrowRight size={12} style={{ color: REL_COLORS[rel.type] || '#94A3B8' }} />
                        <span className="text-[10px] px-1.5 py-0.5 rounded border" style={{ borderColor: REL_COLORS[rel.type] || '#1E2A45', color: REL_COLORS[rel.type] || '#94A3B8', backgroundColor: `${REL_COLORS[rel.type] || '#94A3B8'}15` }}>{rel.type}</span>
                        <ArrowRight size={12} style={{ color: REL_COLORS[rel.type] || '#94A3B8' }} />
                        <span className="font-mono text-xs text-[var(--nexus-text-primary)]">{asset.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(!relData?.outgoing?.length && !relData?.incoming?.length) && (
                <div className="text-center py-8 text-[var(--nexus-text-muted)] text-xs">No relationships mapped for this CI</div>
              )}
            </div>
          </div>

          {/* Impact Analysis */}
          {showImpact && impactData && (
            <div className="nexus-card relative overflow-hidden mt-3 animate-nexus-slide-in">
              <div className="p-3 border-b border-[var(--nexus-border)]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={14} className="text-[var(--nexus-danger)]" />
                    <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Impact Analysis</h2>
                  </div>
                  <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-[var(--nexus-danger)] bg-opacity-15 text-[var(--nexus-danger)]">
                    BLAST RADIUS: {impactData.blast_radius} services
                  </span>
                </div>
              </div>
              <div className="p-3">
                {impactData.impacted_services?.length > 0 ? (
                  <div className="space-y-1.5">
                    {impactData.impacted_services.map((svc, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-red-500/10">
                        <div className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-[var(--nexus-danger)]"></span>
                          <span className="font-mono text-xs text-[var(--nexus-text-primary)]">{svc.asset?.name}</span>
                        </div>
                        <span className="text-[10px] text-[var(--nexus-text-muted)]">{svc.asset?.type} • {svc.asset?.environment}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-[var(--nexus-text-muted)] text-center py-4">No impacted services identified</p>}
                {impactData.affected_tickets?.length > 0 && (
                  <div className="mt-3">
                    <h3 className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium mb-1">Active Tickets Affecting This CI</h3>
                    {impactData.affected_tickets.map(t => (
                      <Link key={t.id} to={`/incidents/${t.id}`} className="block text-xs font-mono text-[var(--nexus-primary-bright)] hover:underline">{t.id}: {t.title}</Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const InfoRow = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-[11px] text-[var(--nexus-text-muted)]">{label}</span>
    <span className="text-[11px] font-mono text-[var(--nexus-text-secondary)]">{value}</span>
  </div>
);
